import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import { useRef } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';

const StyledButton = styled(Button)`
  text-align: left;
  justify-content: space-between;
`;
const Option = styled(ButtonBase)`
  width: 100%;
  text-align: left;
  gap: var(--unit);
  min-width: 320px;
  padding: var(--intermediate-unit) var(--double-unit);
  transition: 0.2s ease-in-out;

  [role='dialog'] & {
    &:first-of-type {
      border-radius: var(--intermediate-unit) var(--intermediate-unit) 0 0;
    }
    &:last-of-type {
      border-radius: 0 0 var(--intermediate-unit) var(--intermediate-unit);
    }
  }

  &.active,
  &:hover {
    background: var(--c-nd-50);
  }
`;

type Props<T> = {
  value: T | undefined;
  options: {
    value: NoInfer<T>;
    label: string | ReactNode;
    disabled?: boolean;
    to?: To;
    replace?: boolean;
  }[];
  onChange?: (value: NoInfer<T>) => void;
  placeholder?: string | ReactNode;
  renderButton?: (props: {
    children: ReactNode;
    open: boolean;
    ref: React.RefObject<HTMLButtonElement | null>;
  }) => ReactNode;
  align?: 'left' | 'right' | 'center';
} & Pick<
  ComponentProps<typeof Button>,
  'disabled' | 'size' | 'id' | 'startIcon'
>;

export const DropdownSelect = <T extends string>({
  value,
  options,
  onChange,
  placeholder,
  renderButton,
  align = 'left',
  ...buttonProps
}: Props<T>) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(option => option.value === value);

  const triggerContent = selectedOption?.label || placeholder;

  return (
    <Dropdown
      gap={4}
      align={align}
      label={({ open, ...dropdownTriggerProps }): ReactNode => {
        const children =
          typeof triggerContent === 'string' ? (
            <LabelM bold>{triggerContent}</LabelM>
          ) : (
            triggerContent
          );

        const defaultButton = (
          <StyledButton
            ref={btnRef}
            disableDebounce
            color="tertiary"
            className={classNames({ active: open })}
            endIcon={<FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
            {...dropdownTriggerProps}
            {...buttonProps}
          >
            {children}
          </StyledButton>
        );

        if (renderButton) {
          return renderButton({
            children,
            open,
            ref: btnRef,
            ...dropdownTriggerProps,
          });
        }

        return defaultButton;
      }}
    >
      {({ closeDropdown }) => (
        <Vertical gap={0}>
          {options.map(
            ({ value: optionValue, label, disabled, to, replace }) => {
              return (
                <Option
                  key={optionValue}
                  className={classNames({ active: optionValue === value })}
                  disabled={disabled}
                  onClick={() => {
                    onChange?.(optionValue);
                    closeDropdown();
                  }}
                  {...(to && {
                    to,
                    replace,
                  })}
                >
                  {typeof label === 'string' ? (
                    <LabelM bold>{label}</LabelM>
                  ) : (
                    label
                  )}
                </Option>
              );
            }
          )}
        </Vertical>
      )}
    </Dropdown>
  );
};
